<template>
  <div class="franck-hertz-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>ΔE = hν = 4.9 eV (Hg)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="canvas-box simulation">
        <svg width="100%" height="200" viewBox="0 0 400 200">
          <!-- Tube background -->
          <rect x="50" y="40" width="300" height="120" rx="60" fill="rgba(100, 100, 255, 0.05)" stroke="rgba(255,255,255,0.2)" />
          
          <!-- Mercury Atoms -->
          <g v-for="atom in atoms" :key="atom.id">
            <circle :cx="atom.x" :cy="atom.y" r="4" :fill="atom.excited ? '#ff4757' : '#888'" />
            <circle v-if="atom.excited" :cx="atom.x" :cy="atom.y" r="8" fill="none" stroke="#ff4757" opacity="0.3">
               <animate attributeName="r" from="4" to="12" dur="0.5s" repeatCount="indefinite" />
            </circle>
          </g>

          <!-- Electrons -->
          <g v-for="e in electrons" :key="e.id">
            <circle :cx="e.x" :cy="e.y" r="2" fill="#00d2ff" />
          </g>

          <!-- Grid and Collecting Anode -->
          <line x1="280" y1="40" x2="280" y2="160" stroke="#ffd700" stroke-dasharray="2" />
          <line x1="330" y1="60" x2="330" y2="140" stroke="#888" stroke-width="4" />
          
          <text x="260" y="30" fill="#ffd700" font-size="10">栅极</text>
          <text x="320" y="30" fill="#888" font-size="10">板极</text>
        </svg>

        <!-- Dynamic Curve -->
        <div class="curve-container">
           <svg width="100%" height="120" viewBox="0 0 400 120">
              <path :d="curvePath" fill="none" stroke="#00d2ff" stroke-width="2" />
              <circle :cx="activeX" :cy="activeY" r="4" fill="#ff4757" />
              <text x="350" y="110" fill="#888" font-size="10">U_G (V)</text>
              <text x="10" y="20" fill="#888" font-size="10">I (Current)</text>
           </svg>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="control-card glass">
          <div class="row">
             <label>加速电压 U_G: {{ voltage }} V</label>
             <el-slider v-model="voltage" :min="0" :max="30" step="0.1" size="mini"></el-slider>
          </div>
          <div class="row legend">
             <span class="dot blue"></span> 自由电子
             <span class="dot grey"></span> 汞原子
             <span class="dot red"></span> 激发的汞原子
          </div>
        </div>

        <div class="discovery-note" v-latex>
          <p><strong>物理详解：</strong>当加速电压达到 **4.9V** 的整数倍时，电子与汞原子发生**非弹性碰撞**将其激发，动能几乎全部损失，导致到达板极的电流陡降。这直接证明了原子内部能量是**量子化**的。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FranckHertzViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      voltage: 15,
      atoms: Array.from({ length: 15 }, (_, i) => ({
        id: i,
        x: 100 + Math.random() * 150,
        y: 60 + Math.random() * 80,
        excited: false
      })),
      electrons: Array.from({ length: 10 }, (_, i) => ({
        id: i,
        x: 50 + Math.random() * 40,
        y: 60 + Math.random() * 80,
        speed: 1
      })),
      timer: null
    }
  },
  computed: {
    curvePath() {
      let d = "M 0 110 ";
      for (let x = 0; x <= 400; x += 5) {
        let u = x / 13; // Map pixel to voltage
        // Current oscillates with 4.9V peaks
        let i = u * 4 - Math.sin((u / 4.9) * 2 * Math.PI) * 10;
        let y = 110 - i * 0.8;
        d += `L ${x} ${y} `;
      }
      return d;
    },
    activeX() { return this.voltage * 13; },
    activeY() {
        let u = this.voltage;
        let i = u * 4 - Math.sin((u / 4.9) * 2 * Math.PI) * 10;
        return 110 - i * 0.8;
    }
  },
  methods: {
    animate() {
      this.electrons.forEach(e => {
        e.x += (this.voltage / 10) + 1;
        if (e.x > 330) {
            e.x = 50;
            e.y = 60 + Math.random() * 80;
        }
        
        // Collision logic
        this.atoms.forEach(atom => {
          let dist = Math.sqrt(Math.pow(e.x - atom.x, 2) + Math.pow(e.y - atom.y, 2));
          if (dist < 10) {
              // Probability of excitation depends on voltage near 4.9 multiples
              let nearPeak = Math.abs((this.voltage % 4.9)) < 0.5 || Math.abs((this.voltage % 4.9) - 4.9) < 0.5;
              if (nearPeak) {
                  atom.excited = true;
                  setTimeout(() => { atom.excited = false; }, 500);
              }
          }
        });
      });
      this.timer = requestAnimationFrame(this.animate);
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { cancelAnimationFrame(this.timer); }
}
</script>

<style scoped lang="scss">
.franck-hertz-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.canvas-box { background: #020205; border-radius: 12px; position: relative; border: 1px solid rgba(255,255,255,0.05); }
.curve-container { margin-top: -30px; padding: 0 15px; }
.legend { display: flex; align-items: center; gap: 10px; font-size: 0.65rem; color: #888; 
  .dot { width: 6px; height: 6px; border-radius: 50%; }
  .blue { background: #00d2ff; }
  .grey { background: #888; }
  .red { background: #ff4757; }
}
.discovery-note { background: rgba(0, 210, 255, 0.05); padding: 12px 18px; border-radius: 12px; p { margin: 0; font-size: 0.75rem; color: #aaa; line-height: 1.5; strong { color: #fff; } } }
</style>
